/** Session plumbing for the dashboard login. Cookies ride automatically on
 *  same-origin fetch; nothing here stores credentials. */
export async function checkSession(): Promise<boolean> {
  // Only a DEFINITE "not authenticated" answer evicts the board: 401 (no/bad
  // cookie) or 403. Anything else -- a 502/504 from a restarting collector,
  // a network failure, a malformed response -- must fail OPEN rather than
  // bouncing an already-authenticated operator to the login form with no way
  // back short of retyping credentials. nginx's `auth_request` is the real
  // gate on every data route regardless of what this returns, so leaving the
  // shell mounted through a backend blip leaks nothing; App.tsx's own
  // `dash:unauthorized` listener still catches a session that turns out to
  // actually be dead the moment any panel's own fetch hits nginx and 401s.
  try {
    const res = await fetch('/auth/check', { headers: { Accept: 'application/json' } })
    return res.status !== 401 && res.status !== 403
  } catch {
    return true
  }
}

export async function login(username: string, password: string): Promise<void> {
  const res = await fetch('/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  })
  if (!res.ok) {
    throw new Error(
      res.status === 401 ? 'Invalid credentials'
      : res.status === 429 ? 'Too many attempts — wait a minute'
      : res.status === 503 ? 'Login is not configured on the server'
      : `HTTP ${res.status}`)
  }
}

export async function logout(): Promise<void> {
  await fetch('/auth/logout', { method: 'POST' })
}
